/**
 * Focus Strip Component
 * The #1 priority display at the top of the dashboard
 */

import React from 'react'
import { Button } from '../ui/button'
import { cn } from '../../lib/utils'
import { 
  Target, Clock, AlertCircle, ArrowRight, Play, CheckCircle2
} from 'lucide-react'

interface FocusPriority {
  id: string
  title: string
  reason: string
  estimatedMinutes: number
}

interface Statistics {
  critical: number
  urgent: number
  totalToday: number
  streak: number
}

interface FocusStripProps {
  priority: FocusPriority | null
  statistics: Statistics
  onStartFocus: () => void
  focusMode: boolean
}

export default function FocusStrip({
  priority,
  statistics,
  onStartFocus,
  focusMode
}: FocusStripProps) {
  return (
    <div className="px-8 py-6 bg-gray-50 border-b border-gray-200">
      <div className="max-w-7xl mx-auto">
        {priority ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
            <div className="flex items-center justify-between">
              {/* Priority Task Info */}
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-2 h-2 bg-pink-500 rounded-full"></div>
                  <span className="text-sm font-medium text-gray-600 uppercase tracking-wide">
                    Focus Priority
                  </span>
                </div>
                
                <h1 className="text-2xl font-semibold text-gray-900 mb-2 leading-tight">
                  {priority.title}
                </h1>
                
                <p className="text-gray-600 mb-4 leading-relaxed">
                  {priority.reason}
                </p>
                
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Clock className="w-4 h-4" />
                  <span>{priority.estimatedMinutes} minutes</span>
                </div>
              </div>
              
              {/* Action Button */}
              <div className="ml-8">
                <Button
                  onClick={onStartFocus}
                  size="lg"
                  className="bg-pink-500 hover:bg-pink-600 text-white px-8 py-4 text-base font-medium shadow-sm transition-colors"
                >
                  <Play className="w-5 h-5 mr-2" />
                  {focusMode ? 'Continue Focus' : "Start Working"}
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
            <Target className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Ready to focus?
            </h2>
            <p className="text-gray-600 mb-6">
              Let me find your most important task
            </p>
            <Button
              onClick={onStartFocus}
              className="bg-pink-500 hover:bg-pink-600 text-white px-6 py-3 font-medium"
            >
              Find My Priority
            </Button>
          </div>
        )}
        
        {/* Quick Stats */}
        {statistics && (
          <div className="mt-6 grid grid-cols-4 gap-4">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 text-center">
              <div className="text-2xl font-bold text-gray-900">{statistics.critical}</div>
              <div className="text-sm text-gray-500 mt-1">Critical</div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 text-center">
              <div className="text-2xl font-bold text-gray-900">{statistics.urgent}</div>
              <div className="text-sm text-gray-500 mt-1">Urgent</div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 text-center">
              <div className="text-2xl font-bold text-pink-600">{statistics.totalToday}</div>
              <div className="text-sm text-gray-500 mt-1">Today</div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 text-center">
              <div className="text-2xl font-bold text-gray-900">{statistics.streak}</div>
              <div className="text-sm text-gray-500 mt-1">Day Streak</div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}